// @ts-ignore
import React, { SourceHTMLAttributes } from "react";

import { useIntl } from "react-intl";
import { SupportedLocales } from "src/utils";

import FeatureGates from "@atlassian/feature-gate-js-client";
import { ProductKeys } from "@atlassiansox/asc-stargate";

import { CrossFlowTouchpointProvider } from "@/components/cross-flow/touchpoint";
import { HeaderTagTypes } from "@/components/wpl/content-block/src";
import { AspectRatios } from "@/components/wpl/image/src";
import { IndividualTabPropsQuote } from "@/components/wpl/tab-group/src";
import { IntercomProvider } from "@/libraries/intercom/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import { IconNameTypes, Layout } from "@/libraries/wpl/foundations/src";

import { getLocalizedImage } from "../../../../localization/localizable-images/localized-images-product-discovery";
import { JiraProductDiscoveryTemplate } from "../../../../templates/jira-product-discovery-software-page/src";

export const JiraProductDiscoveryPage = () => {
    const intl = useIntl();
    const isBootIntercom: boolean = FeatureGates.checkGate(
        "gwp-1304-add-intercom-to-jpd-product-tour"
    );

    const intercomAppId = intl.locale === "ja" ? "p0ww63i2" : "odnoznr4";

    const props = {
        sideBySideSectionProps: {
            headerHeaderText: intl.formatMessage({
                id: "JiraProductDiscoveryPage.sideBySideSection.headerHeaderText",
                defaultMessage: "Discover your next great product idea"
            }),
            headerBodyText: intl.formatMessage({
                id: "JiraProductDiscoveryPage.sideBySideSection.headerBodyText",
                defaultMessage:
                    "Better product decisions are made when they’re made as a team."
            }),
            alternatingTextAndImageProps: {
                imageTextSideRowOnePaddingBelowLargeScreen: "32px 0 0",
                imageTextSideRowOnePadding: "32px 0 16px",
                imageTextSideRowTwoPaddingBelowLargeScreen: "32px 0 0",
                imageTextSideRowTwoPadding: "64px 0 16px",
                imageTextSideRowThreePaddingBelowLargeScreen: "32px 0 16px",
                imageTextSideRowThreePadding: "64px 0 64px",
                imageWidth: "100%",
                rowHeaderType: "h3" as HeaderTagTypes,
                rowPropsOdd: "8-4" as Layout,
                rowPropsEven: "4-8" as Layout,

                rowOneHeader: intl.formatMessage({
                    id: "JiraProductDiscoveryPage.sideBySideSection.rowOneHeader",
                    defaultMessage: "Bring structure to chaos"
                }),
                rowOneBody: intl.formatMessage({
                    id: "JiraProductDiscoveryPage.sideBySideSection.firstTextBody",
                    defaultMessage:
                        "Bring the best ideas to life by gathering data and insights that help your team prioritize what will make the most impact."
                }),
                rowOneImageLink: getLocalizedImage(
                    "row1",
                    intl.locale as SupportedLocales
                ),
                rowOneImageAltText: intl.formatMessage({
                    id: "JiraProductDiscoveryPage.sideBySideSection.rowOneImageAltText",
                    defaultMessage: "Impact assessment screenshot"
                }),
                rowOneEyebrowText: intl.formatMessage({
                    id: "JiraProductDiscoveryPage.sideBySideSection.rowOneEyebrowText",
                    defaultMessage: "NOT FEELING CONFIDENT?"
                }),

                rowTwoHeader: intl.formatMessage({
                    id: "JiraProductDiscoveryPage.sideBySideSection.secondTextHeader",
                    defaultMessage: "Get everyone on the same page"
                }),
                rowTwoBody: intl.formatMessage({
                    id: "JiraProductDiscoveryPage.sideBySideSection.secondTextBody",
                    defaultMessage:
                        "Spend less time updating and persuading using custom views and roadmaps that give your stakeholders the what, the when, and the why. Easily share read-only views with external stakeholders."
                }),
                rowTwoImageLink: getLocalizedImage(
                    "row2",
                    intl.locale as SupportedLocales
                ),
                rowTwoImageAltText: intl.formatMessage({
                    id: "JiraProductDiscoveryPage.sideBySideSection.rowTwoImageAltText",
                    defaultMessage: "Performance screenshot"
                }),
                rowTwoEyebrowText: intl.formatMessage({
                    id: "JiraProductDiscoveryPage.sideBySideSection.rowTwoEyebrowText",
                    defaultMessage: "SPENDING TOO MUCH TIME UPDATING?"
                }),

                rowThreeHeader: intl.formatMessage({
                    id: "JiraProductDiscoveryPage.sideBySideSection.rowThreeHeader",
                    defaultMessage: "Bridge the gap - in Jira"
                }),
                rowThreeBody: intl.formatMessage({
                    id: "JiraProductDiscoveryPage.sideBySideSection.rowThreeBody",
                    defaultMessage:
                        "Bring business and tech teams together by connecting product ideas here to the delivery work happening in Jira Software."
                }),
                rowThreeImageLink: getLocalizedImage(
                    "row3",
                    intl.locale as SupportedLocales
                ),
                rowThreeImageAltText: intl.formatMessage({
                    id: "JiraProductDiscoveryPage.sideBySideSection.rowThreeImageAltText",
                    defaultMessage: "Goals screenshot"
                }),
                rowThreeEyebrowText: intl.formatMessage({
                    id: "JiraProductDiscoveryPage.sideBySideSection.rowThreeEyebrowText",
                    defaultMessage: "SPEAKING DIFFERENT LANGUAGES?"
                })
            }
        },
        tabSectionProps: {
            headerText: intl.formatMessage({
                id: "JiraProductDiscoveryPage.tabsSection.headerText",
                defaultMessage: "How it works"
            }),
            tabs: [
                {
                    textLabel: intl.formatMessage({
                        id: "JiraProductDiscovery.capture.textLabel",
                        defaultMessage: "Capture"
                    }),
                    isForceDisplayTextLabelOnPill: true,
                    id: "ideas-and-insights",
                    headerText: intl.formatMessage({
                        id: "JiraProductDiscovery.capture.tabsHeader",
                        defaultMessage: "Capture ideas and insights"
                    }),
                    bodyText: intl.formatMessage({
                        id: "JiraProductDiscovery.capture.tabsBody",
                        defaultMessage:
                            "Product teams - you now have a dedicated home for your work. Gather and organize your product opportunities, user feedback, and feature requests all in one place."
                    }),
                    quoteImageProps: {
                        className: "tab-quote-image",
                        src: "https://wac-cdn.atlassian.com/dam/jcr:5412ebd3-6135-4109-9ffc-666961b9bad2/quote.svg?cdnVersion=1220",
                        alt: intl.formatMessage({
                            id: "JiraProductDiscovery.capture.tabs.quote.iconAlt",
                            defaultMessage: "Quotation mark"
                        })
                    },
                    quote: intl.formatMessage({
                        id: "JiraProductDiscovery.capture.tabs.quote.text",
                        defaultMessage:
                            "Jira Product Discovery is helping us do the right things, in the right order, and at the right time, focusing on work that will deliver on benefits"
                    }),
                    isDisplayQuoteInSmScreen: true,
                    name: intl.formatMessage({
                        id: "JiraProductDiscovery.capture.tabs.quote.name",
                        defaultMessage: "JOHN SAYERS"
                    }),
                    company: intl.formatMessage({
                        id: "JiraProductDiscovery.capture.tabs.quote.titleAndCompany",
                        defaultMessage: "PMO Lead, RenaissanceRe"
                    }),
                    display: {
                        imageProps: {
                            src: getLocalizedImage(
                                "tab1",
                                intl.locale as SupportedLocales
                            ),
                            alt: intl.formatMessage({
                                id: "JiraProductDiscovery.capture.imageAltText",
                                defaultMessage:
                                    "Improve discoverability of configuration options"
                            }),
                            aspectRatio: "none"
                        }
                    }
                },
                {
                    textLabel: intl.formatMessage({
                        id: "JiraProductDiscovery.prioritize.textLabel",
                        defaultMessage: "Prioritize"
                    }),
                    isForceDisplayTextLabelOnPill: true,
                    id: "easy-prioritization",
                    headerText: intl.formatMessage({
                        id: "JiraProductDiscovery.prioritize.tabsHeader",
                        defaultMessage: "Prioritize – your way"
                    }),
                    bodyText: intl.formatMessage({
                        id: "JiraProductDiscovery.prioritize.tabsBody",
                        defaultMessage:
                            "Flexible views and granular fields let you compare ideas against one another using the prioritization method that works best for you."
                    }),
                    quoteImageProps: {
                        className: "tab-quote-image",
                        src: "https://wac-cdn.atlassian.com/dam/jcr:5412ebd3-6135-4109-9ffc-666961b9bad2/quote.svg?cdnVersion=1220",
                        alt: intl.formatMessage({
                            id: "JiraProductDiscovery.prioritize.tabs.quote.iconAlt",
                            defaultMessage: "Quotation mark"
                        })
                    },
                    quote: intl.formatMessage({
                        id: "JiraProductDiscovery.prioritize.tabs.quote.text",
                        defaultMessage:
                            'Up until Jira Product Discovery, even with other roadmapping tools, we\'ve had to use shared spreadsheets and slide presentations to coordinate prioritization in an accessible way. However, within a week of "trying" out JPD it became clear it was the tool to invest in going forward'
                    }),
                    isDisplayQuoteInSmScreen: true,
                    name: intl.formatMessage({
                        id: "JiraProductDiscovery.prioritize.tabs.quote.name",
                        defaultMessage: "JOHN DAILY JR"
                    }),
                    company: intl.formatMessage({
                        id: "JiraProductDiscovery.prioritize.tabs.quote.titleAndCompany",
                        defaultMessage: "Head of Product, Karhoo"
                    }),
                    display: {
                        imageProps: {
                            src: getLocalizedImage(
                                "tab2",
                                intl.locale as SupportedLocales
                            ),
                            alt: intl.formatMessage({
                                id: "JiraProductDiscovery.prioritize.imageAltText",
                                defaultMessage: "Priority matrix"
                            }),
                            aspectRatio: "none"
                        }
                    }
                },
                {
                    textLabel: intl.formatMessage({
                        id: "JiraProductDiscovery.engage.textLabel",
                        defaultMessage: "Engage"
                    }),
                    isForceDisplayTextLabelOnPill: true,
                    id: "custom-roadmaps",
                    headerText: intl.formatMessage({
                        id: "JiraProductDiscovery.engage.tabsHeader",
                        defaultMessage: "Engage and align your teams"
                    }),
                    bodyText: intl.formatMessage({
                        id: "JiraProductDiscovery.engage.tabsBody",
                        defaultMessage:
                            "Create and share roadmaps tailored to individual stakeholders, and let them provide feedback via comments, votes, or simple reactions."
                    }),
                    quoteImageProps: {
                        className: "tab-quote-image",
                        src: "https://wac-cdn.atlassian.com/dam/jcr:5412ebd3-6135-4109-9ffc-666961b9bad2/quote.svg?cdnVersion=1220",
                        alt: intl.formatMessage({
                            id: "JiraProductDiscovery.engage.tabs.quote.iconAlt",
                            defaultMessage: "Quotation mark"
                        })
                    },
                    quote: intl.formatMessage({
                        id: "JiraProductDiscovery.engage.tabs.quote.text",
                        defaultMessage:
                            "Jira Product Discovery has revitalized how we think about our roadmap"
                    }),
                    isDisplayQuoteInSmScreen: true,
                    name: intl.formatMessage({
                        id: "JiraProductDiscovery.engage.tabs.quote.name",
                        defaultMessage: "Adam Chapman"
                    }),
                    company: intl.formatMessage({
                        id: "JiraProductDiscovery.engage.tabs.quote.titleAndCompany",
                        defaultMessage: "Full Stack Developer, Apex Networks"
                    }),
                    display: {
                        imageProps: {
                            src: getLocalizedImage(
                                "tab3",
                                intl.locale as SupportedLocales
                            ),
                            alt: intl.formatMessage({
                                id: "JiraProductDiscovery.engage.imageAltText",
                                defaultMessage: "Roadmap screenshot"
                            }),
                            aspectRatio: "none"
                        }
                    }
                },
                {
                    textLabel: intl.formatMessage({
                        id: "JiraProductDiscovery.connect.textLabel",
                        defaultMessage: "Connect"
                    }),
                    isForceDisplayTextLabelOnPill: true,
                    id: "all-on-jira",
                    headerText: intl.formatMessage({
                        id: "JiraProductDiscovery.connect.tabsHeader",
                        defaultMessage: "Connect teams, all in Jira"
                    }),
                    bodyText: intl.formatMessage({
                        id: "JiraProductDiscovery.connect.tabsBody",
                        defaultMessage:
                            "Seamlessly connect your product roadmap and ideas with epics in Jira Software so teams have context and visibility from discovery through to delivery."
                    }),
                    quoteImageProps: {
                        className: "tab-quote-image",
                        src: "https://wac-cdn.atlassian.com/dam/jcr:5412ebd3-6135-4109-9ffc-666961b9bad2/quote.svg?cdnVersion=1220",
                        alt: intl.formatMessage({
                            id: "JiraProductDiscovery.connect.tabs.quote.iconAlt",
                            defaultMessage: "Quotation mark"
                        })
                    },
                    quote: intl.formatMessage({
                        id: "JiraProductDiscovery.connect.tabs.quote.text",
                        defaultMessage:
                            "Jira Product Discovery combines everything I was doing in spreadsheets and Powerpoints into one easy-to-navigate tool. Plus everything is added straight into Jira, instead of requiring some third-party tool to integrate"
                    }),
                    isDisplayQuoteInSmScreen: true,
                    name: intl.formatMessage({
                        id: "JiraProductDiscovery.connect.tabs.quote.name",
                        defaultMessage: "Abby Kiesling"
                    }),
                    company: intl.formatMessage({
                        id: "JiraProductDiscovery.connect.tabs.quote.titleAndCompany",
                        defaultMessage: "Product Manager, EZ Texting"
                    }),
                    display: {
                        imageProps: {
                            src: getLocalizedImage(
                                "tab4",
                                intl.locale as SupportedLocales
                            ),
                            alt: intl.formatMessage({
                                id: "JiraProductDiscovery.connect.imageAltText",
                                defaultMessage: "Roadmap screenshot"
                            }),
                            aspectRatio: "none"
                        }
                    }
                }
            ] as IndividualTabPropsQuote[]
        },
        workflowSectionProps: {
            iconName: "checkmarkcircle" as IconNameTypes,
            iconColor: "#091e42",
            headerText: intl.formatMessage({
                id: "JiraProductDiscoveryPage.workflowSection.headerText",
                defaultMessage: "Connect your entire workflow with Jira"
            }),
            bodyText: intl.formatMessage({
                id: "JiraProductDiscoveryPage.workflowSection.bodyText",
                defaultMessage:
                    "Jira brings it all together with dedicated tools for every team."
            }),
            pictureImg: {
                sources: [
                    {
                        media: "(max-width: 47.99rem)",
                        srcSet: getLocalizedImage(
                            "workFlowMobile",
                            intl.locale as SupportedLocales
                        ),
                        type: "image/png"
                    } as SourceHTMLAttributes<HTMLSourceElement>,
                    {
                        media: "(min-width: 48rem)",
                        srcSet: getLocalizedImage(
                            "workFlow",
                            intl.locale as SupportedLocales
                        ),
                        type: "image/webp"
                    } as SourceHTMLAttributes<HTMLSourceElement>
                ],
                image: {
                    src: getLocalizedImage(
                        "workFlow",
                        intl.locale as SupportedLocales
                    ),
                    alt: intl.formatMessage({
                        id: "JiraProductDiscoveryPage.workflowSection.imageAltText",
                        defaultMessage:
                            "Jira product discovery workflow diagram"
                    }),
                    aspectRatio: "none" as AspectRatios
                }
            }
        },
        cTASectionProps: {
            pronouncedCtaSectionProps: {
                headerText: intl.formatMessage({
                    id: "JiraProductDiscoveryPage.pronouncedCtaSection.cta.headerText",
                    defaultMessage: "Join us in the Atlassian Community"
                }),
                bodyText: intl.formatMessage({
                    id: "JiraProductDiscoveryPage.pronouncedCtaSection.cta.bodyText",
                    defaultMessage:
                        "Ask questions, give product feedback, and connect with our team and other users to learn best practices."
                }),
                pictureProps: {
                    image: {
                        src: getLocalizedImage(
                            "pronouncedCta",
                            intl.locale as SupportedLocales
                        ),
                        alt: intl.formatMessage({
                            id: "JiraProductDiscoveryPage.pronouncedCtaSection.cta.altText",
                            defaultMessage: "High five illustration"
                        }),
                        aspectRatio: "none" as AspectRatios
                    }
                },
                ctaText: intl.formatMessage({
                    id: "JiraProductDiscoveryPage.pronouncedCtaSection.cta.linkText",
                    defaultMessage: "Join the discussion"
                }),
                ctaHref:
                    "https://community.atlassian.com/t5/Jira-Product-Discovery/gh-p/jira-product-discovery"
            },
            ctaProps: {
                headerText: intl.formatMessage({
                    id: "JiraProductDiscoveryPage.ctaProps.cta.headerText",
                    defaultMessage: "Build better with Jira Product Discovery"
                }),
                bodyText: intl.formatMessage({
                    id: "JiraProductDiscoveryPage.ctaProps.cta.bodyText",
                    defaultMessage:
                        "No more guesswork. No more reactive roadmaps. Only engaged teams building great products, with confidence."
                }),
                ctaText: intl.formatMessage({
                    id: "JiraProductDiscoveryPage.ctaProps.cta.buttonText",
                    defaultMessage: "Get it free"
                }),
                ctaHref: useLocalizedHref(
                    "/try/cloud/signup?bundle=jira-product-discovery"
                )
            }
        }
    };

    return (
        <IntercomProvider appId={intercomAppId} triggerBoot={isBootIntercom}>
            <CrossFlowTouchpointProvider
                screenName="wacJpdProductTourPage"
                productKeys={[ProductKeys.JIRA_PRODUCT_DISCOVERY]}
            >
                <JiraProductDiscoveryTemplate {...props} />
            </CrossFlowTouchpointProvider>
        </IntercomProvider>
    );
};
